/**
 * FairwayBid – das Wetter als Arbeitsinformation
 *
 * Warum überhaupt Wetter auf einem Beschaffungsportal?
 *
 * Weil es bei Golfanlagen den Tagesablauf bestimmt. Ein Clubmanager, der bei
 * Sonne auf dem Platz ist, sitzt bei Regen im Büro – und im Büro werden
 * Ausschreibungen geschrieben. Für eine Anlage ist das Wetter der eigene
 * Tag; für einen Händler ist es die Prognose, wie viel heute hereinkommt.
 *
 * Deshalb zwei verschiedene Auskünfte aus derselben Quelle:
 *
 *   Golfanlage  ihr Wetter, für ihren Ort
 *   Händler     Norden und Süden getrennt, dazu die Erwartung ans Aufkommen
 *
 * Die Formulierung bleibt eine Vermutung und gibt sich als solche: "kann",
 * "erfahrungsgemäß". Eine Zahl zu versprechen, die wir nicht kennen, wäre
 * der schnellste Weg, dem Portal nicht mehr zu glauben.
 *
 * Quelle ist Open-Meteo: kein Schlüssel, keine Anmeldung, keine
 * Nutzerdaten - wir fragen nach Koordinaten, nicht nach Personen.
 *
 * Reine Logik, kein React und kein fetch – damit prüfbar.
 */

/**
 * Die WMO-Wettercodes, gruppiert auf das, was man unterscheiden muss.
 *
 * Open-Meteo liefert 28 Codes. Für "heute drinnen oder draußen" reichen
 * sieben Lagen; feiner zu unterscheiden würde nur eine Genauigkeit
 * vortäuschen, die eine Tagesprognose nicht hat.
 */
export const LAGEN = {
  klar: { symbol: 'sonne', label: 'Klar' },
  heiter: { symbol: 'heiter', label: 'Heiter' },
  bewoelkt: { symbol: 'wolke', label: 'Bewölkt' },
  nebel: { symbol: 'nebel', label: 'Nebel' },
  regen: { symbol: 'regen', label: 'Regen' },
  schnee: { symbol: 'schnee', label: 'Schnee' },
  gewitter: { symbol: 'gewitter', label: 'Gewitter' },
};

const CODE_ZU_LAGE = {
  0: 'klar',
  1: 'klar',
  2: 'heiter',
  3: 'bewoelkt',
  45: 'nebel',
  48: 'nebel',
  51: 'regen',
  53: 'regen',
  55: 'regen',
  56: 'regen',
  57: 'regen',
  61: 'regen',
  63: 'regen',
  65: 'regen',
  66: 'regen',
  67: 'regen',
  71: 'schnee',
  73: 'schnee',
  75: 'schnee',
  77: 'schnee',
  80: 'regen',
  81: 'regen',
  82: 'regen',
  85: 'schnee',
  86: 'schnee',
  95: 'gewitter',
  96: 'gewitter',
  99: 'gewitter',
};

/** Welche Lage gehört zu diesem Code? Unbekanntes gilt als bewölkt. */
export const lageFuer = code => {
  const key = CODE_ZU_LAGE[Number(code)];
  return key ? { key, ...LAGEN[key] } : { key: 'bewoelkt', ...LAGEN.bewoelkt };
};

/**
 * Ist das ein Tag, an dem niemand freiwillig auf dem Platz steht?
 *
 * Nebel zählt bewusst nicht dazu: Er hängt morgens und ist mittags weg.
 */
export const istSchlechtwetter = lage =>
  ['regen', 'schnee', 'gewitter'].includes(lage?.key || lage);

/**
 * Formt die Tagesantwort von Open-Meteo in das, was eine Kachel zeigt.
 *
 * @param {Object} daily die "daily"-Antwort
 * @returns {Object|null}
 */
export const tagesaussicht = daily => {
  const code = daily?.weather_code?.[0] ?? daily?.weathercode?.[0];
  if (code === undefined || code === null) return null;

  const lage = lageFuer(code);
  const max = daily?.temperature_2m_max?.[0];
  const min = daily?.temperature_2m_min?.[0];
  const regenWahrscheinlich =
    daily?.precipitation_probability_max?.[0] ?? daily?.precipitation_probability_mean?.[0] ?? null;

  const runde = t => (typeof t === 'number' ? Math.round(t) : null);

  return {
    ...lage,
    maxTemp: runde(max),
    minTemp: runde(min),
    regenRisiko: typeof regenWahrscheinlich === 'number' ? regenWahrscheinlich : null,
    schlecht: istSchlechtwetter(lage),
  };
};

/**
 * "14 bis 21 °C" - oder das, was vorhanden ist.
 *
 * Zwischen Zahl und Einheit steht ein geschütztes Leerzeichen. Auf einem
 * Telefon ist die Kachel halb so breit wie der Bildschirm, und dort brach
 * die Zeile sonst zwischen "14" und "°C" um - die Einheit stand allein in
 * der nächsten Zeile.
 */
const NBSP = '\u00A0';

export const temperaturText = aussicht => {
  const { minTemp, maxTemp } = aussicht || {};
  if (typeof maxTemp === 'number' && typeof minTemp === 'number') {
    return `${minTemp} bis ${maxTemp}${NBSP}°C`;
  }
  if (typeof maxTemp === 'number') return `bis ${maxTemp}${NBSP}°C`;
  return null;
};

/**
 * Die Erwartung ans Anfrageaufkommen – für Händler.
 *
 * Die Regel ist einfach und wird auch so erklärt: Wo es regnet, sitzen die
 * Beschaffer im Büro. Sie steht bewusst im Konjunktiv.
 *
 * @param {Object} nord Tagesaussicht Norddeutschland
 * @param {Object} sued Tagesaussicht Süddeutschland
 * @returns {{ton: 'mehr'|'weniger'|'gemischt', text: string}|null}
 */
export const aufkommenHinweis = (nord, sued) => {
  if (!nord && !sued) return null;

  const nassNord = !!nord?.schlecht;
  const nassSued = !!sued?.schlecht;

  if (nassNord && nassSued) {
    return {
      ton: 'mehr',
      text:
        'In Nord und Süd ist das Wetter heute schlecht. Clubmanager und Beschaffer haben dann eher Bürozeit – rechnen Sie mit mehr Anfragen als an einem trockenen Tag.',
    };
  }

  if (nassNord || nassSued) {
    const wo = nassNord ? 'Norden' : 'Süden';
    return {
      ton: 'gemischt',
      text: `Im ${wo} ist das Wetter heute schlecht. Dort haben Clubmanager und Beschaffer eher Bürozeit – von da kann heute mehr hereinkommen als sonst.`,
    };
  }

  return {
    ton: 'weniger',
    text:
      'In Nord und Süd ist es heute trocken. Dann sind Clubmanager eher auf dem Platz als am Schreibtisch – es kann sein, dass heute etwas weniger Anfragen hereinflattern.',
  };
};

/**
 * Der Satz für die Golfanlage selbst.
 *
 * Kein Rat, nur eine Feststellung mit einem Anschluss. Wer bei Regen im
 * Büro sitzt, weiß das selbst; ihm zu sagen "nutzen Sie die Zeit" wäre
 * belehrend.
 */
export const clubHinweis = aussicht => {
  if (!aussicht) return null;
  return aussicht.schlecht
    ? 'Ein Tag für den Schreibtisch – gute Gelegenheit, eine Ausschreibung fertig zu machen.'
    : 'Gutes Wetter für den Platz.';
};
